import React, { useState, useEffect } from "react";
import { api, getActiveSubdomain } from "@/lib/api";
import { Routes, Route, useParams } from "react-router-dom";
import { LayoutDashboard, Users, Package, UserPlus, Receipt, CalendarClock, Crown, Bell, Smartphone, Zap, FileText, BookOpen, ArrowRightLeft, MessageSquare, Landmark, LifeBuoy, Globe } from "lucide-react";
import DashLayout from "@/components/DashLayout";
import Dashboard from "./Dashboard";
import Customers from "./Customers";
import Dealers from "./Dealers";
import Agents from "./Agents";
import Products from "./Products";


import Recharges from "./Recharges";
import Expiring from "./Expiring";
import Subscription from "./Subscription";
import Notifications from "./Notifications";
import SimDetails from "./SimDetails";
import AdminRecharge from "./AdminRecharge";
import RechargeLogs from "./RechargeLogs";
import WalletLedger from "./WalletLedger";
import BalanceTransfers from "./BalanceTransfers";
import AdminComplaints from "./Complaints";
import Settlements from "./Settlements";
import SitePanel from "./SitePanel";

const nav = [
  { to: ".", label: "Dashboard", icon: LayoutDashboard, end: true },

  // People
  { to: "customers", label: "Customers", icon: Users, group: "People" },
  { to: "dealers", label: "Dealers & Staff", icon: UserPlus, group: "People" },
  { to: "agents", label: "Agents", icon: UserPlus, group: "People" },

  // Operations
  { to: "products", label: "Products", icon: Package, group: "Operations" },
  { to: "sim-details", label: "SIM Details", icon: Smartphone, group: "Operations" },
  { to: "recharge", label: "Recharge", icon: Zap, group: "Operations" },
  { to: "expiring", label: "Expiring Soon", icon: CalendarClock, group: "Operations" },

  // Finance
  { to: "recharges", label: "Transactions", icon: Receipt, group: "Finance" },
  { to: "recharge-logs", label: "Recharge Logs", icon: FileText, group: "Finance" },
  { to: "settlements", label: "Settlement Report", icon: Landmark, group: "Finance" },
  { to: "wallet-ledger", label: "Wallet Ledger", icon: BookOpen, group: "Finance" },
  { to: "balance-transfers", label: "Balance Transfers", icon: ArrowRightLeft, group: "Finance" },

  // Settings & Support
  { to: "subscription", label: "Subscription", icon: Crown, group: "Settings" },
  { to: "notifications", label: "Notifications", icon: Bell, group: "Settings" },
  { to: "complaints", label: "Complaints", icon: MessageSquare, group: "Settings" },
  { to: "site", label: "Site Settings", icon: Globe, group: "Settings" },
  { to: "support", label: "Platform Support", icon: LifeBuoy, group: "Settings" },
];

export default function AdminPage() {
  const { subdomain: paramSubdomain } = useParams();
  const activeSubdomain = getActiveSubdomain();
  const subdomain = activeSubdomain || paramSubdomain;
  const basePath = activeSubdomain ? "/admin" : `/site/${subdomain}/admin`;
  return (
    <DashLayout navItems={nav} title="Admin (LCO)" basePath={basePath}>
      <Routes>
        <Route index element={<Dashboard />} />
        <Route path="customers" element={<Customers />} />
        <Route path="dealers" element={<Dealers />} />
        <Route path="agents" element={<Agents />} />
        <Route path="products" element={<Products />} />

        <Route path="sim-details" element={<SimDetails />} />
        <Route path="recharges" element={<Recharges />} />
        <Route path="recharge" element={<AdminRecharge />} />
        <Route path="recharge-logs" element={<RechargeLogs />} />
        <Route path="settlements" element={<Settlements />} />
        <Route path="wallet-ledger" element={<WalletLedger />} />
        <Route path="balance-transfers" element={<BalanceTransfers />} />
        <Route path="expiring" element={<Expiring />} />
        <Route path="subscription" element={<Subscription />} />
        <Route path="notifications" element={<Notifications />} />
        <Route path="complaints" element={<AdminComplaints />} />
        <Route path="site" element={<SitePanel />} />
        <Route path="support" element={<PlatformSupport />} />
      </Routes>
    </DashLayout>
  );
}

function PlatformSupport() {
  const [support, setSupport] = useState(null);
  useEffect(() => { api.get("/admin/platform-support").then(r => setSupport(r.data)).catch(() => {}); }, []);

  return (
    <div className="max-w-xl">
      <div className="mb-6">
        <h3 className="font-heading font-bold text-2xl text-brand-nav">Platform Support</h3>
        <p className="text-slate-500 mt-1">Contact the platform owner for assistance.</p>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
        {support ? (
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Support Email</label>
              <div className="font-medium text-slate-800">{support.support_email || "Not provided"}</div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Support Phone</label>
              <div className="font-medium text-slate-800">{support.support_phone || "Not provided"}</div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Support WhatsApp</label>
              <div className="font-medium text-slate-800">{support.support_whatsapp || "Not provided"}</div>
            </div>
          </div>
        ) : (
          <div className="text-slate-400 text-sm">Loading support details...</div>
        )}
      </div>
    </div>
  );
}
